import React, { useState, useEffect } from 'react';
import { supermercadoService, batchScheduleService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

function BatchScheduleAdmin() {
  const [supermercados, setSupermercados] = useState([]);
  const [schedules, setSchedules] = useState({});
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [success, setSuccess] = useState('');
  const [loadingSupermercados, setLoadingSupermercados] = useState(true);
  const [savingSchedule, setSavingSchedule] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoadingSupermercados(true);
    setError('');
    setErrorTrace(null);
    setDebugInfo(null);
    
    try {
      // Fetch supermercados
      const supermercadosResponse = await supermercadoService.getAllSupermercados();
      if (supermercadosResponse.success) {
        setSupermercados(supermercadosResponse.data || []);
      }
      
      // Fetch schedules
      const schedulesResponse = await batchScheduleService.getAllSchedules();
      if (schedulesResponse.success) {
        const schedulesMap = {};
        (schedulesResponse.data || []).forEach(schedule => {
          schedulesMap[schedule.supermercadoId] = schedule;
        });
        setSchedules(schedulesMap);
      }
    } catch (err) {
      const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al cargar los datos');
      setError(message);
      setErrorTrace(trace);
      setDebugInfo(debug);
    } finally {
      setLoadingSupermercados(false);
    }
  };

  const handleScheduleChange = (supermercadoId, field, value) => {
    setSchedules(prev => ({
      ...prev,
      [supermercadoId]: {
        ...prev[supermercadoId],
        supermercadoId: supermercadoId,
        [field]: value
      }
    }));
  };

  const handleSaveSchedule = async (supermercadoId) => {
    const schedule = schedules[supermercadoId];
    
    if (!schedule || !schedule.scheduledTime) {
      setError('Debe especificar una hora de ejecución');
      return;
    }
    
    setSavingSchedule(prev => ({ ...prev, [supermercadoId]: true }));
    setError('');
    setErrorTrace(null);
    setDebugInfo(null);
    setSuccess('');
    
    try {
      const scheduleData = {
        supermercadoId: supermercadoId,
        scheduledTime: schedule.scheduledTime,
        enabled: schedule.enabled !== undefined ? schedule.enabled : true
      };
      
      const response = await batchScheduleService.createOrUpdateSchedule(scheduleData);
      
      if (response.success) {
        setSuccess(`Horario guardado exitosamente para ${response.data.supermercadoNombre}`);
        // Update the schedule in state
        setSchedules(prev => ({
          ...prev,
          [supermercadoId]: response.data
        }));
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Error al guardar el horario');
      }
    } catch (err) {
      const { message, errorTrace: trace, debugInfo: debug } = extractErrorDetails(err, 'Error al guardar el horario');
      setError(message);
      setErrorTrace(trace);
      setDebugInfo(debug);
    } finally {
      setSavingSchedule(prev => ({ ...prev, [supermercadoId]: false }));
    }
  };

  const handleDeleteSchedule = async (supermercadoId) => {
    const schedule = schedules[supermercadoId];
    
    if (!schedule || !schedule.id) {
      return;
    }
    
    if (!window.confirm('¿Está seguro de eliminar este horario programado?')) {
      return;
    }
    
    setSavingSchedule(prev => ({ ...prev, [supermercadoId]: true }));
    setError('');
    setSuccess('');
    
    try {
      const response = await batchScheduleService.deleteSchedule(schedule.id);
      
      if (response.success) {
        setSuccess('Horario eliminado exitosamente');
        // Remove schedule from state
        setSchedules(prev => {
          const newSchedules = { ...prev };
          delete newSchedules[supermercadoId];
          return newSchedules;
        });
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Error al eliminar el horario');
      }
    } catch (err) {
      const { message } = extractErrorDetails(err, 'Error al eliminar el horario');
      setError(message);
    } finally {
      setSavingSchedule(prev => ({ ...prev, [supermercadoId]: false }));
    }
  };

  return (
    <div className="batch-schedule-admin">
      <h2>Administración de Horarios de Actualización Batch</h2>
      <p className="info-text">
        Configure los horarios automáticos para la ejecución del proceso de actualización masiva de stock
        por supermercado. El proceso se ejecutará automáticamente en el horario configurado.
      </p>

      <ErrorDisplay error={error} errorTrace={errorTrace} debugInfo={debugInfo} />
      
      {success && (
        <div className="success-section">
          <p className="success-message">{success}</p>
        </div>
      )}

      {loadingSupermercados ? (
        <p>Cargando supermercados...</p>
      ) : (
        <div className="schedules-list">
          <table className="schedules-table">
            <thead>
              <tr>
                <th>Supermercado</th>
                <th>Código</th>
                <th>Hora de Ejecución</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {supermercados.map((supermercado) => {
                const schedule = schedules[supermercado.id] || { 
                  supermercadoId: supermercado.id,
                  scheduledTime: '',
                  enabled: true
                };
                const isSaving = savingSchedule[supermercado.id];
                
                return (
                  <tr key={supermercado.id} className={!supermercado.activo ? 'inactive-row' : ''}>
                    <td>{supermercado.nombre}</td>
                    <td>{supermercado.codigo || '-'}</td>
                    <td>
                      <input
                        type="time"
                        value={schedule.scheduledTime || ''}
                        onChange={(e) => handleScheduleChange(supermercado.id, 'scheduledTime', e.target.value)}
                        disabled={isSaving || !supermercado.activo}
                        className="time-input"
                      />
                    </td>
                    <td>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={schedule.enabled !== false}
                          onChange={(e) => handleScheduleChange(supermercado.id, 'enabled', e.target.checked)}
                          disabled={isSaving || !supermercado.activo}
                        />
                        <span className="slider"></span>
                      </label>
                      <span className="status-text">
                        {schedule.enabled !== false ? 'Habilitado' : 'Deshabilitado'}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => handleSaveSchedule(supermercado.id)}
                        disabled={isSaving || !supermercado.activo || !schedule.scheduledTime}
                      >
                        {isSaving ? 'Guardando...' : (schedule.id ? 'Actualizar' : 'Guardar')}
                      </button>
                      {schedule.id && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteSchedule(supermercado.id)}
                          disabled={isSaving}
                          style={{ marginLeft: '5px' }}
                        >
                          Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {supermercados.length === 0 && (
            <p className="info-text">No hay supermercados registrados.</p>
          )}
        </div>
      )}
      
      <div className="info-section" style={{ marginTop: '30px' }}>
        <h3>Información Importante</h3>
        <ul>
          <li>El proceso batch se ejecutará automáticamente a la hora especificada.</li>
          <li>La hora debe estar en formato 24 horas (HH:mm), por ejemplo: 14:30</li>
          <li>Solo los supermercados activos pueden tener horarios configurados.</li>
          <li>Puede habilitar o deshabilitar un horario sin eliminarlo.</li>
          <li>El proceso utilizará las credenciales del portal externo configuradas en el sistema.</li>
        </ul>
      </div>
    </div>
  );
}

export default BatchScheduleAdmin;
